import React, { useEffect, useMemo, useState } from 'react';

import { AuditEntry, searchAudit } from '../api/ops-console';
import {
  Drawer,
  Empty,
  Field,
  FilterBar,
  Kpi,
  KpiRow,
  Note,
  OpsPage,
  Pill,
  Row,
  Table,
  Value,
} from '../../../design-system/ops';

/**
 * The audit log — ADM-16-FR-01 through FR-04.
 *
 * The screen's own subtitle is the point of it: this is a compliance
 * obligation, not a feature. Every row is an event that already happened and
 * cannot be edited, and the drawer says so in as many words rather than
 * leaving an operator to wonder whether they could correct a mistake here.
 */

const ACTION_LABEL: Record<string, string> = {
  RELEASE: 'Escrow liberado',
  REFUND: 'Devolución',
  HOLD: 'Retención',
  SUSPEND: 'Suspensión',
  FLAG: 'Marcado',
  ACTIVATE: 'Activación',
  PROBATION: 'Probación',
  NOTIFY: 'Notificación',
  RESOLVE: 'Resuelto',
  ROLE_ASSIGNED: 'Roles asignados',
  ROLE_REVOKED: 'Acceso retirado',
  ELEVATION_GRANTED: 'Permiso elevado',
  ELEVATION_REVOKED: 'Elevación revocada',
  PARAMETER_CHANGED: 'Parámetro cambiado',
  SESSION_TERMINATED: 'Sesión cerrada',
  SESSION_ANOMALY: 'Acceso inusual',
  AUDIT_EXPORTED: 'Auditoría exportada',
  LEGAL_HOLD_PLACED: 'Retención legal',
  LEGAL_HOLD_LIFTED: 'Retención legal levantada',
  SHIFT_HANDOVER: 'Relevo de turno',
  CASE_CLAIMED: 'Caso asignado',
  CASE_RELEASED: 'Caso devuelto a la cola',
  CASE_REASSIGNED: 'Caso reasignado',
  CASE_BLOCKED: 'Caso bloqueado',
  CASE_RESUMED: 'Caso retomado',
  CASE_ESCALATED: 'Caso escalado',
  CASE_CLOSED: 'Caso cerrado',
  CONTEXT_SWITCHED: 'Cambio de alumno',
  CONTEXT_CLEARED: 'Vista de padre',
  CONTEXT_DENIED: 'Cambio de alumno rechazado',
  COURSE_REVIEW_DECISION: 'Revisión de curso',
  DOCUMENT_ACCESSED: 'Documento consultado',
  BACKGROUND_CHECK_DECISION: 'Decisión de antecedentes',
  EXCEPTION_REQUESTED: 'Excepción solicitada',
  EXCEPTION_APPROVED: 'Excepción aprobada',
  EXCEPTION_REVOKED: 'Excepción revocada',
  EXCEPTION_EXPIRED: 'Excepción vencida',
  LEARNING_RECORD_CORRECTED: 'Registro de aprendizaje corregido',
  LEARNING_PROFILE_ASSIGNED: 'Perfil de aprendizaje asignado',
  RECORDING_ACCESS_GRANTED: 'Grabación vista por la familia',
  RECORDING_ACCESS_DENIED: 'Acceso a grabación rechazado',
  ABSENCE_FOLLOW_UP_SENT: 'Seguimiento de inasistencia enviado',
};

const MONEY_ACTIONS = new Set(['RELEASE', 'REFUND']);
const SAFETY_ACTIONS = new Set(['SUSPEND', 'FLAG', 'BACKGROUND_CHECK_DECISION']);

const AdminAuditLog: React.FC = () => {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [open, setOpen] = useState<AuditEntry | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await searchAudit({});
        if (!cancelled) setEntries(res.entries);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'No se pudo cargar la auditoría');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const shown = useMemo(
    () => (entries ?? []).filter((e) => filter === 'all' || e.action === filter),
    [entries, filter],
  );

  const kinds = useMemo(() => {
    // Only the actions actually present today, so the bar does not offer a
    // filter that returns nothing — the prototype builds it the same way.
    const present: string[] = [];
    for (const entry of entries ?? []) {
      if (!present.includes(entry.action)) present.push(entry.action);
    }
    return [
      { value: 'all', label: 'Todas' },
      ...present.map((a) => ({ value: a, label: ACTION_LABEL[a] ?? a })),
    ];
  }, [entries]);

  if (error) {
    return (
      <OpsPage title="Auditoría">
        <Note tone="bad">{error}</Note>
      </OpsPage>
    );
  }

  if (!entries) {
    return (
      <OpsPage title="Auditoría">
        <Note>Cargando…</Note>
      </OpsPage>
    );
  }

  return (
    <OpsPage
      title="Auditoría"
      subtitle="Cada acción sobre dinero, profesores y menores queda registrada. Es una obligación de cumplimiento, no una función."
    >
      <KpiRow>
        <Kpi label="Acciones hoy" value={entries.length} note="Registradas" />
        <Kpi
          label="Movimientos de dinero"
          value={entries.filter((e) => MONEY_ACTIONS.has(e.action)).length}
          note="Liberación o devolución"
          tone="lav"
        />
        <Kpi
          label="Acciones de seguridad"
          value={entries.filter((e) => SAFETY_ACTIONS.has(e.action)).length}
          note="Suspensiones y marcas"
          tone="bad"
        />
        <Kpi label="Retención" value="7 años" note="Inmutable" tone="ok" />
      </KpiRow>

      <FilterBar label="Filtrar por acción" value={filter} onChange={setFilter} options={kinds} />

      {shown.length === 0 ? (
        <Empty
          icon="📜"
          title="Sin entradas en esta vista"
          detail={filter === 'all' ? 'Nada registrado hoy.' : 'Ninguna acción de ese tipo hoy.'}
        />
      ) : (
        <Table
          columns="130px 150px 1fr 150px 110px"
          head={['Cuándo', 'Acción', 'Objeto y motivo', 'Operador', 'Ref']}
        >
          {shown.map((e) => (
            <Row key={e.auditId} onOpen={() => setOpen(e)} label={`Abrir ${e.objectLabel}`}>
              <span className="mut">{new Date(e.occurredAt).toLocaleString('es-MX')}</span>
              <span>
                <Pill tone={e.highRisk ? 'bad' : 'neu'}>{ACTION_LABEL[e.action] ?? e.action}</Pill>
              </span>
              <span>
                <b>{e.objectLabel}</b>
                <br />
                <span className="mut">{e.reason}</span>
              </span>
              <span className="mut">{e.actorLabel}</span>
              <span className="mut">{e.reference}</span>
            </Row>
          ))}
        </Table>
      )}

      <Drawer
        open={open !== null}
        onClose={() => setOpen(null)}
        title={open ? ACTION_LABEL[open.action] ?? open.action : ''}
        meta={
          open
            ? `${new Date(open.occurredAt).toLocaleString('es-MX')} · ${open.reference}`
            : undefined
        }
      >
        {open ? (
          <>
            <Field label="Objeto">
              <Value>{open.objectLabel}</Value>
            </Field>
            <Field label="Motivo">
              <Note>{open.reason}</Note>
            </Field>
            <Field label="Ejecutado por">
              <Value>{open.actorLabel}</Value>
            </Field>
            <Field label="Correlación">
              {/* The same id every log line for that request carries. It is what
                  makes following a case across layers a join rather than a
                  search — ADM-16-FR-02. */}
              <Value>
                <code>{open.correlationId}</code>
              </Value>
            </Field>
            {open.permissionExercised ? (
              <Field label="Autoridad ejercida">
                <Value>{open.permissionExercised}</Value>
              </Field>
            ) : null}
            {open.parameterVersion ? (
              <Field label="Versión vigente al decidir">
                <Value>
                  <code>{open.parameterVersion}</code>
                </Value>
              </Field>
            ) : null}
            <Field label="Inmutabilidad">
              <Note>
                Esta entrada no puede editarse ni borrarse. Se conserva siete años, como exigen los
                registros que involucran a menores y pagos.
                {open.legalHold
                  ? ` Además está bajo retención legal desde ${new Date(
                      open.legalHold.placedAt,
                    ).toLocaleDateString('es-MX')}: ${open.legalHold.reason}`
                  : ''}
              </Note>
            </Field>
          </>
        ) : null}
      </Drawer>
    </OpsPage>
  );
};

export default AdminAuditLog;
